/**
 * WarehouseStorageList Component
 *
 * Displays a list of storage accounts mapped to a warehouse with management actions.
 * Part of Phase 8: Storage Mapping extension for warehouses.
 */

import React, { useState } from 'react';
import {
  useWarehouseStorage,
  useRemoveWarehouseStorageAccount,
  useSetWarehouseStorageAsPrimary,
  useChangeWarehouseStoragePurpose,
  useDeactivateWarehouseStorageMapping,
  useReactivateWarehouseStorageMapping,
  WarehouseStorageMapping,
  StorageAccountPurpose,
} from '../../hooks/useWarehouseStorage';
import { formatEasternDate } from '@/utils/dateFormatter';
import { NotCollected } from '@/components/ui';

export interface WarehouseStorageListProps {
  warehouseId: string;
  onAddNew: () => void;
  emptyState?: React.ReactNode;
}

// Purpose display labels
const purposeLabels: Record<StorageAccountPurpose, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  archive: 'Archive',
  overflow: 'Overflow',
};

// Purpose colors
const purposeColors: Record<StorageAccountPurpose, string> = {
  primary: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  secondary: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  archive: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  overflow: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

// Status colors
const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  pending_removal: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

// Format bytes to human-readable
function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || bytes === null) return '0 B';
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// Format date relative to now
function formatRelativeDate(date: string | null | undefined): string {
  if (!date) return 'Never';
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return 'Less than an hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatEasternDate(d);
}

export function WarehouseStorageList({ warehouseId, onAddNew, emptyState }: WarehouseStorageListProps) {
  const { data: mappings, isLoading, error, refetch } = useWarehouseStorage(warehouseId);
  const removeStorage = useRemoveWarehouseStorageAccount(warehouseId);
  const setAsPrimary = useSetWarehouseStorageAsPrimary(warehouseId);
  const changePurpose = useChangeWarehouseStoragePurpose(warehouseId);
  const deactivateMapping = useDeactivateWarehouseStorageMapping(warehouseId);
  const reactivateMapping = useReactivateWarehouseStorageMapping(warehouseId);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  // Handle set as primary
  const handleSetPrimary = async (mappingId: string) => {
    try {
      await setAsPrimary.mutateAsync(mappingId);
    } catch {
      // Error handled by mutation
    }
  };

  // Handle change purpose
  const handleChangePurpose = async (mappingId: string, purpose: StorageAccountPurpose) => {
    try {
      await changePurpose.mutateAsync({ mappingId, purpose });
    } catch {
      // Error handled by mutation
    }
  };

  // Handle deactivate
  const handleDeactivate = async (mappingId: string) => {
    try {
      await deactivateMapping.mutateAsync(mappingId);
    } catch {
      // Error handled by mutation
    }
  };

  // Handle reactivate
  const handleReactivate = async (mappingId: string) => {
    try {
      await reactivateMapping.mutateAsync(mappingId);
    } catch {
      // Error handled by mutation
    }
  };

  // Handle remove
  const handleRemove = async (mappingId: string, force: boolean = false) => {
    try {
      await removeStorage.mutateAsync({ mappingId, force });
      setConfirmRemove(null);
    } catch {
      // Error handled by mutation
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className='animate-pulse space-y-4'>
        <div className='h-20 bg-[var(--color-bg-subtle)] rounded-lg'></div>
        <div className='h-20 bg-[var(--color-bg-subtle)] rounded-lg'></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg'>
        <p className='text-red-800 dark:text-red-200'>
          Failed to load storage accounts: {(error as Error).message}
        </p>
        <button
          onClick={() => refetch()}
          className='mt-2 text-sm text-red-600 dark:text-red-400 hover:underline'
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (!mappings || mappings.length === 0) {
    return (
      <div className='text-center py-12 bg-[var(--color-bg-subtle)] rounded-lg border-2 border-dashed border-[var(--color-border)]'>
        <svg
          className='mx-auto h-12 w-12 text-[var(--color-text-secondary)]'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4'
          />
        </svg>
        <h3 className='mt-4 text-lg font-medium text-[var(--color-text-primary)]'>
          No storage accounts configured
        </h3>
        <p className='mt-2 text-sm text-[var(--color-text-secondary)]'>
          Assign a storage account to enable backups for this warehouse.
        </p>
        <button
          onClick={onAddNew}
          className='mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
        >
          Add Storage Account
        </button>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Header with add button */}
      <div className='flex justify-between items-center'>
        <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>
          Storage Accounts ({mappings.length})
        </h3>
        <button
          onClick={onAddNew}
          className='px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
        >
          Add Storage Account
        </button>
      </div>

      {/* Storage list */}
      <div className='space-y-3'>
        {mappings.map((mapping: WarehouseStorageMapping) => (
          <div
            key={mapping.id}
            className={`bg-[var(--color-bg-card)] border rounded-lg overflow-hidden ${
              mapping.isPrimary
                ? 'border-blue-500 ring-1 ring-blue-500'
                : 'border-[var(--color-border)]'
            }`}
          >
            {/* Main row */}
            <div
              className='p-4 flex items-center justify-between cursor-pointer hover:bg-[var(--color-bg-subtle)]'
              onClick={() => setExpandedId(expandedId === mapping.id ? null : mapping.id)}
            >
              <div className='flex items-center gap-4'>
                {/* Storage icon */}
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    mapping.status === 'active'
                      ? 'bg-blue-100 dark:bg-blue-900'
                      : 'bg-gray-100 dark:bg-gray-700'
                  }`}
                >
                  <svg
                    className={`h-5 w-5 ${
                      mapping.status === 'active'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-500'
                    }`}
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4'
                    />
                  </svg>
                </div>

                {/* Storage details */}
                <div>
                  <div className='flex items-center gap-2'>
                    <span className='font-medium text-[var(--color-text-primary)]'>
                      {mapping.storageAccountName || 'Unknown Storage Account'}
                    </span>
                    {mapping.isPrimary && (
                      <span className='px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded'>
                        Primary
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded ${
                        purposeColors[mapping.purpose]
                      }`}
                    >
                      {purposeLabels[mapping.purpose]}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded ${
                        statusColors[mapping.status]
                      }`}
                    >
                      {mapping.status}
                    </span>
                  </div>
                  <div className='mt-1 text-sm text-[var(--color-text-secondary)] flex items-center gap-4'>
                    <span>{mapping.region || 'Unknown region'}</span>
                    <span>&#183;</span>
                    <span>{mapping.containerName || 'default'}</span>
                    <span>&#183;</span>
                    <span>Last backup: {formatRelativeDate(mapping.lastBackupAt)}</span>
                  </div>
                </div>
              </div>

              {/* Stats & expand icon */}
              <div className='flex items-center gap-6'>
                <div className='text-right'>
                  <div className='text-sm font-medium text-[var(--color-text-primary)]'>
                    {formatBytes(mapping.totalStorageBytes)}
                  </div>
                  <div className='text-xs text-[var(--color-text-secondary)]'>
                    {/* Spec 298 FR-006 (row 31): the route counts no warehouse backups and sends null. */}
                    {mapping.backupCount == null ? (
                      <NotCollected reason='The storage-mapping route does not count warehouse backups.' />
                    ) : (
                      `${mapping.backupCount} backups`
                    )}
                  </div>
                </div>
                <svg
                  className={`h-5 w-5 text-[var(--color-text-secondary)] transition-transform ${
                    expandedId === mapping.id ? 'rotate-180' : ''
                  }`}
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </div>
            </div>

            {/* Expanded actions */}
            {expandedId === mapping.id && (
              <div className='px-4 py-3 bg-[var(--color-bg-subtle)] border-t border-[var(--color-border)]'>
                <div className='flex flex-wrap gap-2'>
                  {/* Set as Primary */}
                  {!mapping.isPrimary && mapping.status === 'active' && (
                    <button
                      onClick={() => handleSetPrimary(mapping.id)}
                      disabled={setAsPrimary.isPending}
                      className='px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50'
                    >
                      Set as Primary
                    </button>
                  )}

                  {/* Change Purpose Dropdown */}
                  <select
                    value={mapping.purpose}
                    onChange={(e) =>
                      handleChangePurpose(mapping.id, e.target.value as StorageAccountPurpose)
                    }
                    disabled={changePurpose.isPending}
                    className='px-3 py-1.5 text-sm border border-[var(--color-border)] rounded bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] disabled:opacity-50'
                  >
                    <option value='primary'>Primary</option>
                    <option value='secondary'>Secondary</option>
                    <option value='archive'>Archive</option>
                    <option value='overflow'>Overflow</option>
                  </select>

                  {/* Deactivate/Reactivate */}
                  {mapping.status === 'active' ? (
                    <button
                      onClick={() => handleDeactivate(mapping.id)}
                      disabled={deactivateMapping.isPending}
                      className='px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50'
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReactivate(mapping.id)}
                      disabled={reactivateMapping.isPending}
                      className='px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded hover:bg-green-100 dark:hover:bg-green-900/50 disabled:opacity-50'
                    >
                      Reactivate
                    </button>
                  )}

                  {/* Remove */}
                  {confirmRemove === mapping.id ? (
                    <div className='flex items-center gap-2'>
                      <span className='text-sm text-red-600 dark:text-red-400'>Confirm remove?</span>
                      <button
                        onClick={() => handleRemove(mapping.id, false)}
                        disabled={removeStorage.isPending}
                        className='px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50'
                      >
                        {removeStorage.isPending ? 'Removing...' : 'Yes, Remove'}
                      </button>
                      <button
                        onClick={() => setConfirmRemove(null)}
                        className='px-3 py-1.5 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-subtle)] rounded hover:bg-[var(--color-bg-subtle)]'
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmRemove(mapping.id)}
                      disabled={mapping.isPrimary}
                      className='px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50 disabled:cursor-not-allowed'
                      title={
                        mapping.isPrimary
                          ? 'Cannot remove primary storage. Set another as primary first.'
                          : undefined
                      }
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default WarehouseStorageList;
